import { Box, Text, type TextProps } from "@chakra-ui/react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useOptionalAssistantContext } from "./AssistantProvider";
import { AssistantPanelContext } from "./assistantPanelContext";
import { subjectAwayFromPage } from "./conversationSubject";
import type { PageEntityKind } from "./pageContext";
import { useCurrentPageEntity } from "./useCurrentPageEntity";
import { presentName, useEntityTitle } from "./useEntityTitle";

const useUnnamedEntity = (): Record<PageEntityKind, string> => {
  const { t } = useTranslation();
  return {
    contract: t("this contract"),
    v2Event: t("this procurement"),
    project: t("this project"),
    need: t("this need"),
    organization: t("this company"),
  };
};

export const WorkingOnLabel = (props: TextProps) => {
  const { t } = useTranslation();
  const unnamed = useUnnamedEntity();
  const pageEntity = useCurrentPageEntity();
  const conversationSubject = subjectAwayFromPage(useOptionalAssistantContext()?.subject, pageEntity);
  const conversationSubjectTitle = useEntityTitle(conversationSubject);
  const shownEntity = conversationSubject ?? pageEntity;
  const panel = useContext(AssistantPanelContext);
  const handoff = panel?.handoff;
  const pageSubject = pageEntity ? (presentName(pageEntity.name) ?? unnamed[pageEntity.kind]) : undefined;
  const entitySubject = conversationSubjectTitle ?? pageSubject;
  const stepLabel = shownEntity?.step?.label;
  const ownSubject = handoff?.subject ?? panel?.documentScope?.name;
  if (!ownSubject && !handoff && entitySubject && stepLabel) {
    return (
      <Text
        fontSize="sm"
        color="smMuted"
        display="flex"
        minW="0"
        whiteSpace="nowrap"
        title={t("Working on: {{name}}", { name: `${entitySubject} · ${stepLabel}` }) ?? undefined}
        {...props}
      >
        <Box as="span" flexShrink={0}>
          {t("Working on:")}&nbsp;
        </Box>
        <Box as="span" minW="0" overflow="hidden" textOverflow="ellipsis">
          {entitySubject}
        </Box>
        <Box as="span" flexShrink={0}>
          &nbsp;· {stepLabel}
        </Box>
      </Text>
    );
  }
  const subject = ownSubject ?? entitySubject;
  if (!subject && !handoff) return null;
  return (
    <Text fontSize="sm" color="smMuted" noOfLines={1} {...props}>
      {subject ? t("Working on: {{name}}", { name: subject }) : null}
      {subject && handoff ? " · " : null}
      {handoff ? t("Asking further about: {{source}}", { source: handoff.source }) : null}
    </Text>
  );
};
